import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../../api/client';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Stack,
  Chip,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Assignment as AssignmentIcon } from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import {
  fetchQueuePatterns,
  fetchQueuePatternAssignment,
  QueuePatternAssignment,
  QueueServicePattern,
} from '../../api/queuePatterns';
import QueuePatternAssignmentDialog from '../../components/QueuePatterns/QueuePatternAssignmentDialog';

export const WorkstationsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editItem, setEditItem] = React.useState<any>(null);
  const [form, setForm] = React.useState({ name: '', windowNumber: '', ipAddress: '' });
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [patternAssignments, setPatternAssignments] = React.useState<Record<string, QueuePatternAssignment | null>>({});
  const assignmentLoadedRef = React.useRef<Set<string>>(new Set());
  const [patternDialogState, setPatternDialogState] = React.useState<{ open: boolean; workstation: any | null }>({
    open: false,
    workstation: null,
  });

  const { data, isLoading, error } = useQuery(['operatorWorkstations'], async () => {
    const res = await apiClient.get('/api/operators/workstations');
    return res.data;
  });

  const { data: queuePatternsData } = useQuery(
    ['queue-service-patterns', 'with-inactive'],
    () => fetchQueuePatterns(true),
    { staleTime: 60_000 },
  );
  const queuePatterns: QueueServicePattern[] = queuePatternsData ?? [];

  const createMutation = useMutation((payload: any) => apiClient.post('/api/operators/workstations', payload), {
    onSuccess: () => { queryClient.invalidateQueries(['operatorWorkstations']); setDialogOpen(false); setSnackbar({ open: true, message: 'Касса создана', severity: 'success' }); },
    onError: () => setSnackbar({ open: true, message: 'Ошибка создания', severity: 'error' }),
  });
  const updateMutation = useMutation(({ id, payload }: { id: string; payload: any }) => apiClient.put(`/api/operators/workstations/${id}`, payload), {
    onSuccess: () => { queryClient.invalidateQueries(['operatorWorkstations']); setDialogOpen(false); setSnackbar({ open: true, message: 'Касса обновлена', severity: 'success' }); },
    onError: () => setSnackbar({ open: true, message: 'Ошибка обновления', severity: 'error' }),
  });
  const deleteMutation = useMutation((id: string) => apiClient.delete(`/api/operators/workstations/${id}`), {
    onSuccess: () => { queryClient.invalidateQueries(['operatorWorkstations']); setSnackbar({ open: true, message: 'Касса удалена', severity: 'success' }); },
    onError: () => setSnackbar({ open: true, message: 'Нельзя удалить активную кассу', severity: 'error' }),
  });

  const openCreate = () => { setEditItem(null); setForm({ name: '', windowNumber: '', ipAddress: '' }); setDialogOpen(true); };
  const openEdit = (ws: any) => { setEditItem(ws); setForm({ name: ws.name || '', windowNumber: String(ws.window_number || ''), ipAddress: ws.ip_address || '' }); setDialogOpen(true); };
  const openPatternDialog = (workstation: any) => {
    setPatternDialogState({ open: true, workstation });
    if (workstation?.id) {
      assignmentLoadedRef.current.delete(workstation.id);
    }
  };

  const handleSubmit = () => {
    const payload: any = { name: form.name.trim(), windowNumber: Number(form.windowNumber) };
    if (form.ipAddress) payload.ipAddress = form.ipAddress.trim();
    if (editItem) updateMutation.mutate({ id: editItem.id, payload }); else createMutation.mutate(payload);
  };

  const workstations = React.useMemo(() => data?.data?.workstations || [], [data]);

  React.useEffect(() => {
    const pending = workstations.filter((workstation: any) => workstation?.id && !assignmentLoadedRef.current.has(workstation.id));
    if (pending.length === 0) {
      return;
    }

    let isActive = true;

    (async () => {
      const assignments = await Promise.all(
        pending.map(async (workstation: any) => {
          try {
            const assignment = await fetchQueuePatternAssignment({ workstationId: workstation.id });
            return [workstation.id, assignment] as const;
          } catch (assignmentError) {
            return [workstation.id, null] as const;
          }
        }),
      );

      if (!isActive) {
        return;
      }

      setPatternAssignments((prev) => {
        const next = { ...prev };
        assignments.forEach(([workstationId, assignment]) => {
          next[workstationId] = assignment;
          assignmentLoadedRef.current.add(workstationId);
        });
        return next;
      });
    })();

    return () => {
      isActive = false;
    };
  }, [workstations]);

  return (
    <>
      <Helmet>
        <title>Кассы/Рабочие места - ELEKTROM</title>
      </Helmet>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Кассы / Рабочие места
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Управляйте окнами, к которым привязываются операторы
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Добавить кассу</Button>
        </Box>

        <Card>
          <CardContent>
            {isLoading && <Typography>Загрузка...</Typography>}
            {error && <Alert severity="error">Ошибка загрузки касс</Alert>}
            {!isLoading && !error && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Название</TableCell>
                      <TableCell>Окно</TableCell>
                      <TableCell>IP адрес</TableCell>
                      <TableCell align="center">Шаблон обслуживания</TableCell>
                      <TableCell align="right">Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workstations.map((w: any) => {
                      const assignment = patternAssignments[w.id];
                      const assignmentPattern = assignment
                        ? queuePatterns.find((pattern) => pattern.id === assignment.patternId)
                        : undefined;
                      const assignmentLabel = assignmentPattern
                        ? assignmentPattern.name
                        : assignment
                        ? assignment.patternId
                        : 'По умолчанию';

                      return (
                        <TableRow key={w.id}>
                          <TableCell>{w.name}</TableCell>
                          <TableCell>#{w.window_number}</TableCell>
                          <TableCell>{w.ip_address || '-'}</TableCell>
                          <TableCell align="center">
                            {assignment ? (
                              <Stack spacing={0.5} alignItems="center">
                                <Chip
                                  label={assignmentLabel}
                                  color={assignmentPattern?.isActive ? 'primary' : 'default'}
                                  size="small"
                                />
                                <Typography variant="caption" color="text.secondary">
                                  Позиция: {assignment.positionPointer + 1}
                                </Typography>
                              </Stack>
                            ) : (
                              <Chip label="По умолчанию" size="small" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Назначить шаблон">
                              <IconButton size="small" onClick={() => openPatternDialog(w)}>
                                <AssignmentIcon />
                              </IconButton>
                            </Tooltip>
                            <IconButton size="small" onClick={() => openEdit(w)}><EditIcon /></IconButton>
                            <IconButton size="small" color="error" onClick={() => deleteMutation.mutate(w.id)}><DeleteIcon /></IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editItem ? 'Редактировать кассу' : 'Добавить кассу'}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
              <TextField label="Название" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
              <TextField label="Номер окна" type="number" value={form.windowNumber} onChange={(e) => setForm({ ...form, windowNumber: e.target.value })} fullWidth />
              <TextField label="IP адрес (опционально)" value={form.ipAddress} onChange={(e) => setForm({ ...form, ipAddress: e.target.value })} fullWidth />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
            <Button variant="contained" onClick={handleSubmit} disabled={createMutation.isLoading || updateMutation.isLoading}>
              {editItem ? 'Сохранить' : 'Создать'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
        </Snackbar>

        <QueuePatternAssignmentDialog
          open={patternDialogState.open}
          entityId={patternDialogState.workstation?.id ?? null}
          entityName={patternDialogState.workstation?.name}
          entityType="workstation"
          patterns={queuePatterns}
          onClose={() => setPatternDialogState({ open: false, workstation: null })}
          onAssigned={(assignment) => {
            const workstationId = patternDialogState.workstation?.id;
            if (!workstationId) {
              return;
            }
            setPatternAssignments((prev) => ({
              ...prev,
              [workstationId]: assignment,
            }));
            assignmentLoadedRef.current.add(workstationId);
          }}
        />
      </Box>
    </>
  );
};

export default WorkstationsPage;




